'use client';

import { motion } from 'motion/react';
import { useEffect, useState } from 'react';

import LogoImage from '@app/components/LogoImage';
import { cn } from '@app/libs/utils';

export interface LoadingProps {
  /** Kích thước loading */
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** Văn bản hiển thị */
  text?: string;
  /** Hiển thị logo hay không */
  showLogo?: boolean;
  /** Hiển thị text hay không */
  showText?: boolean;
  /** Màu sắc theme */
  theme?: 'light' | 'dark' | 'auto';
  /** Custom className */
  className?: string;
  /** Custom text className */
  textClassName?: string;
  /** Custom logo className */
  logoClassName?: string;
  /** Tốc độ animation (ms) */
  animationSpeed?: number;
  /** Hiển thị dots animation */
  showDots?: boolean;
  /** Vị trí layout */
  layout?: 'vertical' | 'horizontal';
  /** Position type */
  position?: 'static' | 'relative' | 'absolute' | 'fixed';
  /** Hiển thị overlay background */
  showOverlay?: boolean;
  /** Màu overlay background */
  overlayColor?: string;
  /** Z-index cho overlay */
  zIndex?: number;
  /** Full screen loading */
  fullScreen?: boolean;
  /** Loading state */
  loading?: boolean;
}

export default function Loading({
  size = 'md',
  text = 'Đang tải...',
  showLogo = true,
  showText = true,
  theme = 'auto',
  className = '',
  textClassName = '',
  logoClassName = '',
  animationSpeed = 1000,
  showDots = true,
  layout = 'vertical',
  position = 'static',
  showOverlay = false,
  overlayColor = 'rgba(0, 0, 0, 0.5)',
  zIndex = 50,
  fullScreen = false,
  loading = true,
}: LoadingProps) {
  const [dotCount, setDotCount] = useState(0);

  // Animation cho dots
  useEffect(() => {
    if (!showDots) return;

    const interval = setInterval(() => {
      setDotCount(prev => (prev + 1) % 4);
    }, animationSpeed / 4);

    return () => clearInterval(interval);
  }, [showDots, animationSpeed]);

  // Kích thước dựa trên size prop
  const getSizeConfig = () => {
    const configs = {
      sm: {
        logoSize: 'sm' as const,
        textSize: 'text-sm',
        spacing: 'gap-2',
        logoDimensions: { width: 24, height: 24 },
      },
      md: {
        logoSize: 'md' as const,
        textSize: 'text-base',
        spacing: 'gap-3',
        logoDimensions: { width: 32, height: 32 },
      },
      lg: {
        logoSize: 'lg' as const,
        textSize: 'text-lg',
        spacing: 'gap-4',
        logoDimensions: { width: 48, height: 48 },
      },
      xl: {
        logoSize: 'xl' as const,
        textSize: 'text-xl',
        spacing: 'gap-6',
        logoDimensions: { width: 64, height: 64 },
      },
    };

    return configs[size];
  };

  const sizeConfig = getSizeConfig();

  // Animation variants cho logo
  const logoVariants = {
    animate: {
      rotate: 360,
      scale: [1, 1.1, 1],
      transition: {
        rotate: {
          duration: animationSpeed / 1000,
          repeat: Infinity,
          ease: 'linear' as const,
        },
        scale: {
          duration: animationSpeed / 2000,
          repeat: Infinity,
          ease: 'easeInOut' as const,
        },
      },
    },
  };

  // Animation variants cho text
  const textVariants = {
    animate: {
      opacity: [0.5, 1, 0.5],
      transition: {
        duration: animationSpeed / 1000,
        repeat: Infinity,
        ease: 'easeInOut' as const,
      },
    },
  };

  // Animation variants cho dots
  const dotVariants = {
    animate: (i: number) => ({
      opacity: [0.3, 1, 0.3],
      scale: [0.8, 1.2, 0.8],
      transition: {
        duration: animationSpeed / 2000,
        repeat: Infinity,
        delay: i * 0.2,
        ease: 'easeInOut' as const,
      },
    }),
  };

  const dots = Array.from({ length: 3 }, (_, i) => (
    <motion.span
      key={i}
      custom={i}
      variants={dotVariants}
      animate="animate"
      className="w-1 h-1 bg-current rounded-full"
    />
  ));

  const loadingText = showDots ? `${text}${'.'.repeat(dotCount)}` : text;

  // Nếu loading = false, không render gì
  if (!loading) {
    return null;
  }

  // Tính toán styles cho position
  const getPositionStyles = () => {
    const baseStyles: React.CSSProperties = {
      position,
      zIndex,
    };

    if (position === 'fixed' || position === 'absolute') {
      if (fullScreen) {
        return {
          ...baseStyles,
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: '100vw',
          height: '100vh',
        };
      } else {
        return {
          ...baseStyles,
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
        };
      }
    }

    return baseStyles;
  };

  const positionStyles = getPositionStyles();

  // Nếu có overlay, wrap trong container
  if (showOverlay && (position === 'fixed' || position === 'absolute')) {
    return (
      <div
        className={cn(
          'flex items-center justify-center',
          fullScreen ? 'w-screen h-screen' : 'w-full h-full',
          className
        )}
        style={{
          position,
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: overlayColor,
          zIndex,
        }}
        role="status"
        aria-label="Đang tải"
      >
        <div
          className={cn(
            'flex items-center justify-center',
            layout === 'vertical' ? 'flex-col' : 'flex-row',
            sizeConfig.spacing
          )}
        >
          {/* Logo với animation */}
          {showLogo && (
            <motion.div
              variants={logoVariants}
              animate="animate"
              className={cn('flex-shrink-0', logoClassName)}
            >
              <LogoImage
                variant="animated"
                size={sizeConfig.logoSize}
                theme={theme}
                width={sizeConfig.logoDimensions.width}
                height={sizeConfig.logoDimensions.height}
              />
            </motion.div>
          )}

          {/* Text với animation */}
          {showText && (
            <motion.div
              variants={textVariants}
              animate="animate"
              className={cn(
                'flex items-center',
                sizeConfig.textSize,
                'font-medium text-gray-700 dark:text-gray-300',
                layout === 'vertical' ? 'flex-col' : 'flex-row',
                sizeConfig.spacing,
                textClassName
              )}
            >
              <span>{loadingText}</span>
              {showDots && <div className="flex items-center gap-1">{dots}</div>}
            </motion.div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'flex items-center justify-center',
        layout === 'vertical' ? 'flex-col' : 'flex-row',
        sizeConfig.spacing,
        className
      )}
      style={positionStyles}
      role="status"
      aria-label="Đang tải"
    >
      {/* Logo với animation */}
      {showLogo && (
        <motion.div
          variants={logoVariants}
          animate="animate"
          className={cn('flex-shrink-0', logoClassName)}
        >
          <LogoImage
            variant="animated"
            size={sizeConfig.logoSize}
            theme={theme}
            width={sizeConfig.logoDimensions.width}
            height={sizeConfig.logoDimensions.height}
          />
        </motion.div>
      )}

      {/* Text với animation */}
      {showText && (
        <motion.div
          variants={textVariants}
          animate="animate"
          className={cn(
            'flex items-center',
            sizeConfig.textSize,
            'font-medium text-gray-700 dark:text-gray-300',
            layout === 'vertical' ? 'flex-col' : 'flex-row',
            sizeConfig.spacing,
            textClassName
          )}
        >
          <span>{loadingText}</span>
          {showDots && <div className="flex items-center gap-1">{dots}</div>}
        </motion.div>
      )}
    </div>
  );
}
